import { useState, useCallback, useMemo } from 'react';
import { isValidEmail } from '../utils';
import type { UserData } from '../steps/prepared-by';
import type { Step, QuoteDetailsFormData } from '../types';
import { QuoteDetailsSteps } from '../types';

interface UseQuoteDetailsStepsParams {
  formData: QuoteDetailsFormData;
  currentPreparedData: UserData;
  initialStep: QuoteDetailsSteps;
  quoteType: string;
  // When false, the "Prepared By" step is omitted (e.g. a non-creator viewing
  // the quote can't edit the creator's sales-rep details). Defaults to true.
  canEditPreparedBy?: boolean;
}

export const useQuoteDetailsSteps = ({
  formData,
  currentPreparedData,
  initialStep,
  canEditPreparedBy = true
}: UseQuoteDetailsStepsParams) => {
  // Don't open directly on a step that's hidden for this user (e.g. a
  // non-creator deep-linking to "Prepared By" from the preview) — fall back to
  // the always-visible first step so the editor for a filtered step never shows.
  const safeInitialStep =
    !canEditPreparedBy && initialStep === QuoteDetailsSteps.PREPARED_BY
      ? QuoteDetailsSteps.PREPARED_FOR
      : initialStep;
  const [activeStep, setActiveStep] = useState(safeInitialStep);
  const [showWarning, setShowWarning] = useState(false);

  const steps: Step[] = useMemo(
    () =>
      [
        {
          id: 'prepared-for',
          value: QuoteDetailsSteps.PREPARED_FOR,
          label: 'Prepared For',
          icon: '/icons/quotes-prepared-for.svg',
          completed:
            !!formData.preparedFor?.companyName &&
            !!formData.preparedFor?.address &&
            !!formData?.preparedFor?.phoneNumber,
          hasError:
            !formData.preparedFor?.companyName ||
            !formData.preparedFor?.address ||
            !formData?.preparedFor?.phoneNumber
        },
        ...(canEditPreparedBy
          ? [
              {
                id: 'prepared-by',
                value: QuoteDetailsSteps.PREPARED_BY,
                label: 'Prepared By',
                icon: '/icons/quotes-prepared-by.svg',
                completed:
                  !!currentPreparedData?.firstName &&
                  !!currentPreparedData?.lastName &&
                  !!currentPreparedData?.contactNumber &&
                  isValidEmail(currentPreparedData?.email),
                hasError:
                  !currentPreparedData?.firstName ||
                  !currentPreparedData?.lastName ||
                  !isValidEmail(currentPreparedData?.email) ||
                  !currentPreparedData?.contactNumber
              }
            ]
          : []),
        {
          id: 'expiration-date',
          value: QuoteDetailsSteps.EXPIRATION_DATE,
          label: 'Expiration Date',
          icon: '/icons/quotes-expiration-date.svg',
          completed: !!formData?.expirationDate,
          hasError: false
        },
        {
          id: 'overall-note',
          value: QuoteDetailsSteps.OVERALL_NOTE,
          label: 'Overall Note',
          icon: '/icons/quotes-overall-note.svg',
          completed: true,
          hasError: false
        },
        {
          id: 'quote-display',
          value: QuoteDetailsSteps.QUOTE_DISPLAY,
          label: 'Quote display',
          icon: '/icons/quotes-file-landscape.svg',
          completed: true,
          hasError: false,
          iconWidth: 20,
          iconHeight: 20
        }
      ] as Step[],
    [
      canEditPreparedBy,
      currentPreparedData,
      formData.expirationDate,
      formData.preparedFor.address,
      formData.preparedFor.companyName,
      formData.preparedFor?.phoneNumber
    ]
  );

  // Receives the step's stable value (QuoteDetailsSteps), not the array index.
  const handleStepClick = useCallback((value: number) => {
    setActiveStep(value as QuoteDetailsSteps);
  }, []);

  return {
    steps,
    activeStep,
    handleStepClick,
    showWarning,
    setShowWarning
  };
};
